import { FC, useEffect, useState } from "react";
import { SearchBox } from "../../../components/Interaction/SearchBox/SearchBox";
import { Button } from "../molecules/Button";
import { Checkbox } from "../../../components/Interaction/Checkbox";
import {
  BooleanVoidFunc,
  TypedVoidFunc,
  StateAction,
} from "../../../helpers/functions/types";
import { lowercaseActive } from "../../../helpers";
import { Select } from "./Select";
import {
  AllUiFilters,
  FilterEntry,
  listFilterDescription,
  ListFilters,
  SortOptions,
} from "./index";
import { FilterSelect } from "./FilterSelect";
import { setSelectedFilters, useAppDispatch } from "../../../store";
import { ToggleExtended } from "../general";

export type ListSettingsProps = {
  title: string;
  quantity: number;

  initSort?: SortOptions;
  sortOptions?: SortOptions[];
  handleSort?: TypedVoidFunc<SortOptions>;

  initExtended?: boolean;
  handleExtended?: BooleanVoidFunc;

  searchMessage?: string;
  initSearch?: string;
  handleSearch?: TypedVoidFunc<string>;

  initActiveOnly?: boolean;
  handleActiveOnly?: BooleanVoidFunc;

  activeFilters?: AllUiFilters;
  initFilters?: FilterEntry[];

  listFilters?: listFilterDescription<ListFilters>[];
  feature?: "companies" | "users" | "campaigns";
};

const FILTER_BUTTON_BASE_STYLES =
  "font-normal focus:ring-transparent gap-1 w-max";

const setButtonFilters = (
  setFilters: StateAction<AllUiFilters>,
  filters: AllUiFilters,
  entry: FilterEntry["entry"],
) => {
  const typecastedFilters = filters as (typeof entry)[];
  setFilters(
    typecastedFilters.some((selectedFilter) => selectedFilter === entry)
      ? typecastedFilters.filter((element) => element !== entry)
      : [...typecastedFilters, entry],
  );
};

export const ListSettings: FC<React.PropsWithChildren<ListSettingsProps>> = ({
  title,
  quantity,
  sortOptions,
  initExtended = true,
  handleExtended,
  initSort,
  handleSort,
  searchMessage,
  initSearch = "",
  handleSearch,
  initActiveOnly = false,
  handleActiveOnly,
  activeFilters = [],
  initFilters,
  listFilters,
  feature,
}) => {
  const [extended, setExtended] = useState(initExtended);
  const dispatch = useAppDispatch();

  useEffect(() => {
    handleExtended && handleExtended(extended);
  }, [extended, handleExtended]);

  const [sorted, setSorted] = useState(initSort);
  useEffect(() => {
    sorted && handleSort && handleSort(sorted);
  }, [sorted, handleSort]);

  const [searched, setSearched] = useState(initSearch);
  useEffect(() => {
    handleSearch && handleSearch(searched);
  }, [searched, handleSearch]);

  const [activeOnly, setActiveOnly] = useState(initActiveOnly);
  useEffect(() => {
    handleActiveOnly && handleActiveOnly(activeOnly);
  }, [activeOnly, handleActiveOnly]);

  const [filters, setFilters] = useState(activeFilters);
  useEffect(() => {
    feature && dispatch(setSelectedFilters({ feature, value: filters }));
  }, [filters, feature, dispatch]);

  return (
    <>
      {handleSearch && (
        <SearchBox
          placeholder={`Search ${searchMessage ?? title}`}
          value={searched}
          onChange={(e) => setSearched(e.target.value.toString())}
        />
      )}

      {(handleActiveOnly || initFilters || listFilters) && (
        <div className="mt-4 flex min-w-full max-w-fit flex-wrap justify-between gap-x-2 gap-y-4 align-middle">
          {initFilters && (
            <div className="list-filters no-scrollbar max-sm:-translate-x-4 transform overflow-x-auto max-sm:px-4 w-full">
              <div className="min-w-max sm:min-w-full flex flex-wrap gap-2">
                <Button
                  id="resetFilters"
                  title={`All ${title}`}
                  buttonType={lowercaseActive(filters.length === 0)}
                  className={FILTER_BUTTON_BASE_STYLES}
                  onClick={() => {
                    setFilters([]);
                  }}
                  textStyle="show"
                />
                {initFilters.map((filter, index) => (
                  <Button
                    key={index}
                    buttonType={lowercaseActive(
                      filters.some(
                        (selectedFilter) => selectedFilter === filter.entry,
                      ),
                    )}
                    className={FILTER_BUTTON_BASE_STYLES}
                    title={filter.text ?? filter.entry}
                    onClick={() => {
                      setButtonFilters(setFilters, filters, filter.entry);
                    }}
                    textStyle="show"
                  >
                    <>{filter.icon}</>
                  </Button>
                ))}
              </div>
            </div>
          )}
          {listFilters && (
            <div className="relative flex">
              {listFilters.map((filterSet, index) =>
                FilterSelect(
                  filterSet,
                  filters.length > 0
                    ? filters
                    : listFilters.map((listOptions) => listOptions[0].text),
                  setFilters,
                  index,
                ),
              )}
            </div>
          )}
          {handleActiveOnly && (
            <div className="flex w-fit align-middle">
              <Checkbox
                isChecked={initActiveOnly}
                labelText={`Show only active ${title}`}
                action={(e) => {
                  setActiveOnly(e.target.checked);
                }}
              />
            </div>
          )}
        </div>
      )}
      <hr className="mb-5 mt-6 border-stone-200 dark:border-stone-800" />

      <div className="mb-5 flex justify-between">
        <div className="flex items-center text-xs text-stone-500 dark:text-stone-400">
          {`Found ${quantity} result${quantity !== 1 ? "s" : ""}`}
        </div>

        <div className="flex w-fit">
          {sorted && sortOptions && (
            <Select
              activeOption={sorted}
              options={sortOptions}
              handleSelect={setSorted}
            />
          )}
          {handleExtended && (
            <ToggleExtended setExtended={setExtended} extended={extended} />
          )}
        </div>
      </div>
    </>
  );
};
